import React, { useContext, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styles from "./styles.module.scss";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import CloseIcon from "assets/icons/close.svg?react";
import PersonIcon from "assets/icons/company/person.svg?react";
import CompanyIcon from "assets/icons/company/company.svg?react";
import { ETabs, FC, Props } from "./typings";
import Button from "components/Button";
import { ENotificationType, notification } from "utils/notification";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { ITag } from "dto/ITag";
import PersonForm from "./PersonForm";
import CompanyForm from "./CompanyForm";
import TagBadge from "components/TagBadge";
import clientService, {
  ICreateClient,
  INewUpdateClient,
} from "api/clientService";
import ClientsContext from "contexts/ClientsContext";
import { validateEmail } from "utils/isEmail";
import { IPackage, ISenderDetails } from "dto/IPackage";
import { axiosInstance } from "api/axios";
import SearchLabel from "components/SearchLabel";
import ClientAdditionalAccordion from "components/ClientAdditionalAccordion";
import SignalsContext from "contexts/SignalsContext";
import DownIcon from "assets/icons/arrow-down.svg?react";
import { EClientData, IClient } from "dto/IClient";
import ModalsManagerContext, {
  ClientModalOptions,
} from "contexts/ModalsManager";
import { useTranslation } from "react-i18next";

export interface IAddress {
  street: string;
  zipCode: string;
  city: string;
  address?: string;
}

export interface ICPerson {
  name: string;
  email: string;
  phone: string;
}

export interface IData {
  Case: ICaseSimple | null;
  personNip: string | null;
  personRegon: string | null;
  nip: string | null;
  regon: string | null;
  name: string;
  emails: string[];
  phones: string[];
  EAddresses: string[];
  bank: string[];
  addresses: IAddress[];
  note: string | null;
  contactPerson: ICPerson[];
  tagIds: ITag[];
}

const EditClientModalNew: FC<Props> = ({
  onClose,
  onCreate,
  isCompany = false,
}) => {
  const { t } = useTranslation();
  const [initialValue, setInitialValue] = useState<IClient | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const { options } = useContext(ModalsManagerContext);

  const [actualView, setActualView] = useState(
    isCompany ? ETabs.COMPANY : ETabs.PERSON
  );
  const { mutate: createClient, isLoading: isL } =
    clientService.useCreateClient();
  const { mutate: updateClient, isLoading: isL2 } =
    clientService.useNewUpdateClient();

  const { data: clientData } = clientService.useGetClient(
    clientId ?? "",
    clientId !== null
  );
  const isLoading = isL || isL2;
  const { setCallback } = useContext(SignalsContext);
  const { clients, setClients } = useContext(ClientsContext);

  useEffect(() => {
    if (options) {
      const o = options as ClientModalOptions;

      if (o) {
        setClientId(o.ClientId);
      }
    }
  }, [options]);

  useEffect(() => {
    if (clientData?.data) {
      setInitialValue(clientData.data);
    }
  }, [clientData]);

  const [data, setData] = useState<IData>({
    Case: null,
    personNip: null,
    personRegon: null,
    nip: "",
    regon: "",
    name: "",
    emails: ["", ""],
    phones: ["", ""],
    EAddresses: ["", ""],
    bank: [""],
    addresses: [{ city: "", street: "", zipCode: "", address: "" }],
    contactPerson: [{ email: "", name: "", phone: "" }],
    note: "",
    tagIds: [],
  });

  useEffect(() => {
    if (!initialValue) return;
    setData({
      ...data,
      nip: initialValue?.NIP,
      regon: initialValue?.REGON,
      name: (initialValue?.Name ?? "")
        .replaceAll("<em>", "")
        .replaceAll("</em>", ""),
      emails: initialValue?.Data?.filter(
        (f) => f.Type === EClientData.EMAIL
      ).map((f) => f.Value),
      phones: initialValue?.Data?.filter(
        (f) => f.Type === EClientData.PHONE
      ).map((f) => f.Value),
      EAddresses: initialValue?.Data?.filter(
        (f) => f.Type === EClientData.eaddress
      ).map((f) => f.Value),
    });
  }, [initialValue]);

  useEffect(() => {
    const item = localStorage.getItem("toEdit");
    if (!item) return;
    setInitialValue(JSON.parse(item));
    localStorage.removeItem("toEdit");
  }, []);

  const createData = () => {
    const addresses = data.addresses
      ?.filter(
        (a) => !!a.city?.length && !!a.zipCode?.length && !!a.street?.length
      )
      .map((a, i) => {
        const value = JSON.stringify({
          city: a.city,
          zipCode: a.zipCode,
          address: a.street,
          isHeadquarter: i === 0,
        });
        return {
          Type: "address",
          Value: value,
          IsMain: i === 0,
        };
      });

    const contactPersons = data.contactPerson
      ?.filter((a) => !!a.email.length && !!a.name.length && !!a.phone.length)
      .map((a, i) => {
        const value = JSON.stringify({
          FirstName: a.name?.split?.(" ")?.[0] ?? "",
          LastName: a.name?.split?.(" ")?.[1] ?? "",
          JobPosition: "",
          Phone: a.phone,
          Email: a.email,
        });
        return {
          Type: "contactPerson",
          Value: value,
          IsMain: i === 0,
        };
      });

    const emails = data.emails
      ?.filter((f) => !!f.length)
      .map((m, index) => {
        return {
          Type: "email",
          Value: m.replaceAll(" ", ""),
          IsMain: index === 0,
        };
      });

    const phones = data.phones
      ?.filter((f) => !!f.length)
      ?.map((m, index) => {
        return {
          Type: "phone",
          Value: m.replaceAll(" ", ""),
          IsMain: index === 0,
        };
      });

    const eaddress = data.EAddresses?.filter((f) => !!f.length)?.map(
      (m, index) => {
        return {
          Type: "edeliveryAddress",
          Value: m,
          IsMain: index === 0,
        };
      }
    );

    let d: any = [addresses, emails, phones, contactPersons, eaddress].flat();

    const note = {
      Type: "note",
      Value: data?.note?.length ? data.note : null,
      IsMain: true,
    };

    if (note.Value !== null) {
      d = [...d, note].flat();
    }

    return d;
  };

  const handleCreate = () => {
    if (initialValue) {
      let payload: INewUpdateClient = {
        Id: initialValue.Id,
        Name: data.name.replaceAll("<", "").replaceAll(">", ""),
        Data: createData(),
        IsInstitution: false,
        NIP:
          data.personNip !== null
            ? data.personNip.replaceAll(" ", "")
            : undefined,
        REGON:
          data.personRegon !== null
            ? data.personRegon.replaceAll(" ", "")
            : undefined,
        TagIds: data.tagIds.map((t) => t.Id),
      };

      const nip =
        actualView === ETabs.COMPANY ? data.nip ?? "" : data.personNip ?? "";
      const regon =
        actualView === ETabs.COMPANY
          ? data.regon ?? ""
          : data.personRegon ?? "";

      if (actualView === ETabs.COMPANY) {
        payload = {
          Id: initialValue.Id,
          Name: data.name.replaceAll("<", "").replaceAll(">", ""),
          Data: createData(),
          IsInstitution: true,
          TagIds: data.tagIds.map((t) => t.Id),
          NIP: nip.replaceAll(" ", ""),
          REGON: regon.replaceAll(" ", ""),
        };
      }

      updateClient(payload, {
        onSuccess: (resp) => {
          const newClients = [...clients];
          const client = newClients.find((d) => d.Id === initialValue.Id);
          if (client) {
            client.Name = data.name.replaceAll("<", "").replaceAll(">", "");
            client.Data = createData() as any;
            client.Flags = resp.data.Flags;
            client.NIP =
              actualView === ETabs.PERSON
                ? regon.replaceAll(" ", "") ?? undefined
                : nip.replaceAll(" ", "");
            setClients(newClients);
          }

          onClose();
        },
      });
      return;
    }

    let payload: ICreateClient = {
      Name: data.name,
      Data: createData(),
      IsInstitution: false,
      TagIds: data.tagIds.map((t) => t.Id),
      NIP:
        data.personNip !== null
          ? data.personNip.replaceAll(" ", "")
          : undefined,
      REGON:
        data.personRegon !== null
          ? data.personRegon.replaceAll(" ", "")
          : undefined,
    };

    if (actualView === ETabs.COMPANY) {
      const nip =
        actualView === ETabs.COMPANY ? data.nip ?? "" : data.personNip ?? "";
      const regon =
        actualView === ETabs.COMPANY
          ? data.regon ?? ""
          : data.personRegon ?? "";

      payload = {
        Name: data.name,
        Data: createData(),
        IsInstitution: true,
        TagIds: data.tagIds.map((t) => t.Id),
        NIP: nip.replaceAll(" ", ""),
        REGON: regon.replaceAll(" ", ""),
      };
    }

    createClient(payload, {
      onSuccess: (resp) => {
        setClients([resp.data, ...clients]);
        onCreate?.(resp.data);
        onClose();
        console.log("ustawiam: ", resp.data);
        setCallback(resp.data);
      },
      onError: (resp: any) => {
        if (resp?.response?.data?.errorCodes?.[0]?.code === "Duplicate") {
          notification({
            type: ENotificationType.ERROR,
            title: t("columns.entity"),
            text: t("client.entityDuplicateError"),
          });
          return;
        }
        notification({
          type: ENotificationType.ERROR,
          title: t("columns.entity"),
          text: t("errors.somethingWentWrongShort"),
        });
      },
    });
  };

  const checkCan = () => {
    const wrongEmails = data.emails?.filter(
      (f) => !!f.length && !validateEmail(f)
    );

    if (!data?.name?.length) return true;

    if (wrongEmails?.length) {
      return true;
    }

    return false;
  };

  const refWrapper = useRef(null);

  const [entities, setEntities] = useState([]);

  useEffect(() => {
    (async () => {
      const link = window.location.href.split("/");
      const isThread = window.location.href.includes("thread");
      const packageId = isThread ? link[9] : link[7];
      const packageKind = isThread ? link[7] : link[6];
      const response = await axiosInstance.get(
        `/list?Id=${packageId}&Kind=${packageKind}`
      );

      const pack: IPackage = response.data.Items[0];
      const details: ISenderDetails = pack.SenderDetails;

      setEntities([pack.Receivers, details].flat());
    })();
  }, []);

  const fetchData = async (index: number) => {
    const details = entities[index];
    const newData = { ...data };

    if (details?.IsInstitution) {
      setActualView(ETabs.COMPANY);
    }

    if (details?.Name) {
      newData.name = details.Name;
    }

    if (details?.Email) {
      newData.emails[0] = details.Email;
    }

    if (details?.Nip) {
      newData.nip = details.Nip;
      newData.personNip = details.Nip;
    }
    if (details?.REGON) {
      newData.regon = details.REGON;
      newData.personRegon = details.REGON;
    }

    if (details?.Address) {
      newData.addresses[0] = {
        city: details.Address.city,
        street: details.Address.address ?? "",
        zipCode: details.Address.zipCode,
      };
    }

    newData.bank = [""];

    setData(newData);
  };

  const handleAdd = (
    type: string,
    regon?: string,
    place?: any,
    name?: string
  ) => {
    switch (type) {
      case "all":
        setData({
          ...data,
          name: name ? name : data.name ?? "",
          bank: [""],
          note: data.note ?? "",
          emails: [data?.emails?.[0], data.emails[1] ?? ""],
          phones: [data.phones[0], data.phones[1] ?? ""],
          EAddresses: [data.EAddresses[0], data.EAddresses[1] ?? ""],
          contactPerson: data.contactPerson[0]
            ? [...data.contactPerson]
            : [{ email: "", name: "", phone: "" }],

          addresses: [
            ...data.addresses,
            place ?? { city: "", street: "", zipCode: "" },
          ].filter((e, index) => index === 0 || !!e.city.length),
          regon: regon ?? "",
          personRegon: regon ?? "",
        });

        return;

      case "email":
        setData({ ...data, emails: [...data.emails, ""] });
        return;
      case "phone":
        setData({ ...data, phones: [...data.phones, ""] });
        return;
      case "bank":
        setData({ ...data, bank: [...data.bank, ""] });
        return;
      case "eaddress":
        setData({ ...data, EAddresses: [...data.EAddresses, ""] });
        return;
      case "note":
        setData({ ...data, note: "" });
        return;
      case "person":
        setData({
          ...data,
          contactPerson: [
            ...data.contactPerson,
            { email: "", name: "", phone: "" },
          ],
        });
        return;
      case "address":
        setData({
          ...data,
          addresses: [...data.addresses, { city: "", street: "", zipCode: "" }],
        });
        return;
      case "nip":
        setData({ ...data, personNip: "" });
        return;
      case "regon":
        setData({ ...data, personRegon: "" });
        return;
    }
  };

  const isPackages = window.location.href.includes("incoming");
  const [additionalData, setAdditionalData] = useState(false);


  return ReactDOM.createPortal(
    <ModalLayout
      name="createEntity"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      isAnimation
      ignore
      refWrapper={refWrapper}
      fullHeight={true}
    >
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {initialValue ? t("client.editEntity") : t("client.newEntity")}
          <CloseIcon onClick={onClose} className={styles.closeIcon} />
        </div>
        <div className={styles.content}>
          <div className={styles.switchbox}>
            <div
              onClick={() => setActualView(ETabs.PERSON)}
              className={`${styles.switch}  ${
                actualView === ETabs.PERSON && styles.active
              }`}
            >
              <PersonIcon /> {t("common.person")}
            </div>
            <div
              onClick={() => setActualView(ETabs.COMPANY)}
              className={`${styles.switch}  ${
                actualView === ETabs.COMPANY && styles.active
              }`}
            >
              <CompanyIcon /> {t("client.company")}
            </div>
          </div>
          {actualView === ETabs.PERSON && (
            <div className={styles.formW}>
              <PersonForm
                fetchData={fetchData}
                handleAdd={handleAdd}
                data={data}
                setData={setData}
                isPackages={isPackages}
                entities={entities}
              />
            </div>
          )}
          {actualView === ETabs.COMPANY && (
            <div className={styles.formW}>
              <CompanyForm
                fetchData={fetchData}
                handleAdd={handleAdd}
                data={data}
                setData={setData}
                isPackages={isPackages}
                entities={entities}
              />
            </div>
          )}

          <div className={styles.menus}>
            <SearchLabel
              label={t("forms.role")}
              type={3}
              onChoose={(tag) => {
                setData({ ...data, tagIds: [...data.tagIds, tag!] });
              }}
            />
            {/* {isPackages && (
              <div onClick={fetchData} className={styles.fetch}>
                <Dicon /> Pobierz dane z przesyłki
              </div>
            )} */}
          </div>
          {!!data.tagIds.length && (
            <div className={styles.tags}>
              {data.tagIds.map((t) => (
                <TagBadge
                  key={t.Id}
                  tag={t}
                  onRemove={() => {
                    setData({
                      ...data,
                      tagIds: data.tagIds.filter((te) => te.Id !== t.Id),
                    });
                  }}
                />
              ))}
            </div>
          )}
          <div className={styles.accordion}>
            <div
              onClick={() => setAdditionalData(!additionalData)}
              className={styles.addTitle}
            >
              {t("client.additionalData")}{" "}
              <DownIcon
                className={`${styles.ico} ${additionalData && styles.reverse}`}
              />
            </div>
            <ClientAdditionalAccordion
              handleAdd={handleAdd}
              data={data}
              showed={additionalData}
              setData={setData}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            loading={isLoading}
            disabled={checkCan()}
            onClick={handleCreate}
            text={initialValue ? t("common.save") : t("common.create")}
          />
        </div>
      </div>
    </ModalLayout>,
    document.body
  );
};

export default EditClientModalNew;
